import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';
import { getBallsThrown, getPlayerAverage120, getEquivalent120, getFormaIgraca, getTrendIgraca } from '../utils/statistika';

const SEZONE = ['2025/2026', '2026/2027'];

function Profil({ igracId, igraci, utakmice, treninzi, updateIgrac, setAktivna, jeAdmin, jeGost, povratakNa, globalnaSezona, pocetniTab }) {
  const [aktivniTab, setAktivniTab] = useState(pocetniTab || 'statistike');
  const [editTrening, setEditTrening] = useState(null);
  const [exportOd, setExportOd] = useState('');
  const [exportDo, setExportDo] = useState('');
  const [filterSezona, setFilterSezona] = useState(globalnaSezona || '2025/2026');
  useEffect(() => { setFilterSezona(globalnaSezona); }, [globalnaSezona]);
  const igrac = igraci.find(i => i.id === igracId);

  if (!igrac) return (
    <div className="card empty-state">
      <div className="icon">👤</div>
      <p>Igrac nije pronadjen.</p>
      <button className="btn-secondary" style={{ marginTop: '12px' }}
        onClick={() => setAktivna(povratakNa || { strana: 'igraci' })}>← Nazad</button>
    </div>
  );

  // Sve utakmice igrača (za prikaz karijere u tabici "Utakmice")
  const sveUtakmiceIgraca = utakmice
    .filter(u => u.rezultati?.some(r => r.igracId === igracId))
    .sort((a, b) => a.datum?.localeCompare(b.datum));

  // Utakmice filtrirane po sezoni (za statistike, formu, grafikon)
  const utakmiceIgraca = sveUtakmiceIgraca.filter(u => u.sezona === filterSezona);

  const treninziIgraca = treninzi
    .filter(t => t.rezultati?.some(r => r.igracId === igracId))
    .sort((a, b) => a.datum?.localeCompare(b.datum));

  const treninziSezoneIgraca = treninziIgraca.filter(t => t.sezona === filterSezona);

  const getRezultatIgraca = (utakmica) => utakmica.rezultati?.find(r => r.igracId === igracId);
  const getTreningRezultat = (trening) => trening.rezultati?.find(r => r.igracId === igracId);

  const prosjek120 = getPlayerAverage120(utakmiceIgraca, igracId);
  const prosjekPoSetu = Math.round(prosjek120 / 4);
  const forma = getFormaIgraca(utakmiceIgraca, igracId);
  const trend = getTrendIgraca(utakmiceIgraca, igracId);

  let ukupnoCunjeva = 0, ukupnoSP = 0, ukupnoMP = 0, maxUkupno = 0, ukupnoPromasaja = 0, ukupnoKugli = 0;
  utakmiceIgraca.forEach(u => {
    const r = getRezultatIgraca(u);
    if (r) {
      ukupnoCunjeva += r.ukupno || 0;
      ukupnoSP += r.sp || 0;
      ukupnoMP += r.mp || 0;
      maxUkupno = Math.max(maxUkupno, r.ukupno || 0);
      ukupnoPromasaja += r.promasaji || 0;
      ukupnoKugli += getBallsThrown(r);
    }
  });
  const prosjekPromasaja = ukupnoKugli > 0 ? Math.round((ukupnoPromasaja / ukupnoKugli) * 120 * 10) / 10 : 0;
  const prosjekCunjevaPoUtakmici = Math.round(prosjek120);

  const prosjekTrening = treninziIgraca.length > 0
    ? Math.round(treninziIgraca.reduce((acc, t) => {
        const r = getTreningRezultat(t);
        return acc + (r?.ukupno || 0);
      }, 0) / treninziIgraca.length) : 0;

  // Statistika treninga za odabranu sezonu
  const treningStats = (() => {
    let nastupa = 0, sumUkupno = 0, sumPune = 0, sumCis = 0, sumPromasaji = 0;
    treninziSezoneIgraca.forEach(t => {
      const r = getTreningRezultat(t);
      if (!r) return;
      nastupa++;
      sumUkupno += r.ukupno || 0;
      sumPromasaji += r.promasaji || 0;
      if (r.napredna && r.setovi) {
        [1, 2, 3, 4].forEach(s => {
          sumPune += parseFloat(r.setovi[s]?.pune) || 0;
          sumCis += parseFloat(r.setovi[s]?.cis) || 0;
        });
      } else {
        sumPune += r.ukupnoPune || 0;
        sumCis += r.ukupnoCis || 0;
      }
    });
    if (nastupa === 0) return null;
    return {
      nastupa,
      prosjekUkupno: Math.round(sumUkupno / nastupa),
      prosjekPoSetu: Math.round(sumUkupno / nastupa / 4),
      prosjekPune: Math.round((sumPune / nastupa) * 10) / 10,
      prosjekCis: Math.round((sumCis / nastupa) * 10) / 10,
      prosjekPromasaji: Math.round((sumPromasaji / nastupa) * 10) / 10,
    };
  })();

  const podaciUtakmica = utakmiceIgraca.map(u => {
    const r = getRezultatIgraca(u);
    const kugle = getBallsThrown(r);
    const ekv120 = r ? getEquivalent120(r.ukupno || 0, kugle) : 0;
    return {
      datum: u.datum,
      naziv: u.datum + ' vs ' + u.protivnik,
      ukupno: r?.ukupno || 0,
      ekv120: Math.round(ekv120),
      prosjek: Math.round(ekv120 / 4),
      sp: r?.sp || 0,
      promasaji: r?.promasaji || 0,
    };
  });

  // Normalizovani prosjek/set za sve utakmice (za usporedivost)
  const prosjekUtakmicaGraf = podaciUtakmica.length > 0
    ? Math.round(podaciUtakmica.reduce((a, d) => a + d.prosjek, 0) / podaciUtakmica.length) : 0;
  const maxEkv = podaciUtakmica.length > 0 ? Math.max(...podaciUtakmica.map(d => d.prosjek)) : 0;
  const minEkv = podaciUtakmica.length > 0 ? Math.min(...podaciUtakmica.map(d => d.prosjek)) : 0;

  const podaciTreninzi = treninziIgraca.map(t => {
    const r = getTreningRezultat(t);
    let pune = 0, cis = 0;
    if (r?.napredna && r?.setovi) {
      [1, 2, 3, 4].forEach(s => {
        pune += parseFloat(r.setovi[s]?.pune) || 0;
        cis += parseFloat(r.setovi[s]?.cis) || 0;
      });
    } else {
      pune = r?.ukupnoPune || 0;
      cis = r?.ukupnoCis || 0;
    }
    const ukupno = r?.ukupno || 0;
    return {
      datum: t.datum,
      naziv: t.datum,
      ukupno,
      prosjekPoSetu: Math.round(ukupno / 4),
      promasaji: r?.promasaji || 0,
      pune,
      cis,
    };
  });

  const prosjekTreningGraf = podaciTreninzi.length > 0
    ? Math.round(podaciTreninzi.reduce((a, d) => a + d.ukupno, 0) / podaciTreninzi.length) : 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#16162a', border: '1px solid #2a2a4a', borderRadius: '8px', padding: '10px 14px' }}>
          <p style={{ color: '#e94560', fontWeight: '600', marginBottom: '6px', fontSize: '12px' }}>{label}</p>
          {payload.map(p => (
            <p key={p.name} style={{ color: p.color, fontSize: '13px', margin: '2px 0' }}>
              {p.name}: <strong>{p.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleSacuvajTrening = async (treningId, noviRezultati) => {
    try {
      const trening = treninzi.find(t => t.id === treningId);
      if (!trening) return;
      const azuriraniRezultati = trening.rezultati.map(r =>
        r.igracId === igracId ? { ...r, ...noviRezultati } : r
      );
      await updateDoc(doc(db, 'treninzi', treningId), { rezultati: azuriraniRezultati });
      setEditTrening(null);
      window.location.reload();
    } catch (err) {
      alert('Greska pri cuvanju!');
    }
  };

  const izvezi = () => {
    const treninziZaIzvoz = treninziIgraca.filter(t => {
      if (exportOd && t.datum < exportOd) return false;
      if (exportDo && t.datum > exportDo) return false;
      return true;
    });
    if (treninziZaIzvoz.length === 0) { alert('Nema treninga u odabranom periodu!'); return; }
    let sumU = 0, sumP = 0, sumC = 0, sumPr = 0;
    const redovi = treninziZaIzvoz.map(t => {
      const r = getTreningRezultat(t);
      let pune = 0, cis = 0;
      if (r?.napredna && r?.setovi) {
        [1, 2, 3, 4].forEach(s => { pune += parseFloat(r.setovi[s]?.pune) || 0; cis += parseFloat(r.setovi[s]?.cis) || 0; });
      } else { pune = r?.ukupnoPune || 0; cis = r?.ukupnoCis || 0; }
      const ukupno = r?.ukupno || 0; const promasaji = r?.promasaji || 0;
      sumU += ukupno; sumP += pune; sumC += cis; sumPr += promasaji;
      return { datum: t.datum, klub: t.klub || igrac.klub, ukupno, pune, cis, promasaji, prosjek: Math.round(ukupno / 4) };
    });
    const n = redovi.length;
    const fmt = v => (v === 0 ? '—' : v);
    const period = [exportOd || 'početak', exportDo || 'danas'].join(' – ');
    const genDate = new Date().toLocaleDateString('bs-Latn-BA');

    // SVG grafikon: Ukupno (lijeva os) + Promašaji (desna os)
    const makeSVG = (data) => {
      if (data.length < 2) return '';
      const W = 690, H = 220, pL = 52, pR = 52, pT = 16, pB = 58;
      const cW = W - pL - pR, cH = H - pT - pB;
      const uVals = data.map(d => d.ukupno);
      const prVals = data.map(d => d.promasaji);
      const uMin = Math.max(0, Math.min(...uVals) - 30), uMax = Math.max(...uVals) + 30;
      const prMin = 0, prMax = Math.max(...prVals, 5) + 3;
      const xp = i => pL + (i / (data.length - 1)) * cW;
      const yu = v => pT + cH - ((v - uMin) / (uMax - uMin)) * cH;
      const ypr = v => pT + cH - ((v - prMin) / (prMax - prMin)) * cH;
      const uPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xp(i).toFixed(1)},${yu(d.ukupno).toFixed(1)}`).join(' ');
      const prPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xp(i).toFixed(1)},${ypr(d.promasaji).toFixed(1)}`).join(' ');
      const uDots = data.map((d, i) => `<circle cx="${xp(i).toFixed(1)}" cy="${yu(d.ukupno).toFixed(1)}" r="4.5" fill="#111"/>`).join('');
      const prDots = data.map((d, i) => {
        const cx = xp(i).toFixed(1), cy = ypr(d.promasaji).toFixed(1);
        return `<rect x="${(parseFloat(cx)-4).toFixed(1)}" y="${(parseFloat(cy)-4).toFixed(1)}" width="8" height="8" fill="white" stroke="#111" stroke-width="2"/>`;
      }).join('');
      const xLabels = data.map((d, i) => {
        const x = xp(i).toFixed(1), y = pT + cH + 12;
        return `<text x="${x}" y="${y}" font-size="8.5" fill="#555" text-anchor="end" transform="rotate(-38,${x},${y})">${d.datum}</text>`;
      }).join('');
      const uTicks = [0,1,2,3,4].map(i => {
        const v = Math.round(uMin + (uMax - uMin) * i / 4);
        const y = yu(v).toFixed(1);
        return `<text x="${pL - 5}" y="${(parseFloat(y)+3.5).toFixed(1)}" font-size="9" fill="#111" font-weight="bold" text-anchor="end">${v}</text>
                <line x1="${pL}" y1="${y}" x2="${pL + cW}" y2="${y}" stroke="#ddd" stroke-width="0.8"/>`;
      }).join('');
      const prTicks = [0,1,2,3,4].map(i => {
        const v = Math.round(prMin + (prMax - prMin) * i / 4);
        const y = ypr(v).toFixed(1);
        return `<text x="${pL + cW + 5}" y="${(parseFloat(y)+3.5).toFixed(1)}" font-size="9" fill="#555" font-style="italic" text-anchor="start">${v}</text>`;
      }).join('');
      const avgU = Math.round(sumU / n);
      const avgUY = yu(avgU).toFixed(1);
      return `<svg width="${W}" height="${H}" xmlns="http://www.w3.org/2000/svg">
        <rect width="${W}" height="${H}" fill="white"/>
        ${uTicks}${prTicks}
        <rect x="${pL}" y="${pT}" width="${cW}" height="${cH}" fill="none" stroke="#ccc" stroke-width="1"/>
        <line x1="${pL}" y1="${avgUY}" x2="${pL+cW}" y2="${avgUY}" stroke="#5378ff" stroke-width="1.2" stroke-dasharray="5,3"/>
        <text x="${pL+cW-4}" y="${(parseFloat(avgUY)-4).toFixed(1)}" font-size="8.5" fill="#5378ff" text-anchor="end">Ø ${avgU}</text>
        <path d="${uPath}" fill="none" stroke="#111" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
        <path d="${prPath}" fill="none" stroke="#111" stroke-width="2" stroke-dasharray="7,4" stroke-linejoin="round" stroke-linecap="round"/>
        ${uDots}${prDots}
        ${xLabels}
        <line x1="${pL}" y1="${H-11}" x2="${pL+14}" y2="${H-11}" stroke="#111" stroke-width="2.5"/>
        <circle cx="${pL+7}" cy="${H-11}" r="4.5" fill="#111"/>
        <text x="${pL+20}" y="${H-8}" font-size="9.5" fill="#333">Ukupno (lijeva os)</text>
        <line x1="${pL+135}" y1="${H-11}" x2="${pL+149}" y2="${H-11}" stroke="#111" stroke-width="2" stroke-dasharray="7,4"/>
        <rect x="${pL+138}" y="${H-15}" width="8" height="8" fill="white" stroke="#111" stroke-width="2"/>
        <text x="${pL+155}" y="${H-8}" font-size="9.5" fill="#333">Promašaji (desna os)</text>
      </svg>`;
    };

    const svgGrafikon = n >= 2 ? `
      <div class="chart-title">Grafikon — Ukupno i Promašaji</div>
      ${makeSVG(redovi)}` : '';

    const html = `<!DOCTYPE html><html lang="bs"><head><meta charset="UTF-8">
<title>Statistika treninga — ${igrac.ime}</title>
<style>
@page{size:A4;margin:18mm 15mm}
*{box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;font-size:12px;color:#1a1a1a;margin:0;background:#fff}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:12px;border-bottom:3px solid #1a1a3e}
.logo{font-size:15px;font-weight:900;color:#1a1a3e;letter-spacing:1px}
h1{font-size:20px;font-weight:800;margin:4px 0 2px;color:#1a1a3e}
.subtitle{color:#555;font-size:12px}
.meta{font-size:10px;color:#888;text-align:right}
.stat-grid{display:flex;gap:10px;margin-bottom:18px;flex-wrap:wrap}
.stat-box{border:2px solid #1a1a3e;border-radius:8px;padding:8px 14px;text-align:center;min-width:85px}
.stat-num{font-size:22px;font-weight:800;color:#c0392b}
.stat-lbl{font-size:9px;color:#666;text-transform:uppercase;letter-spacing:.5px;margin-top:2px}
.chart-title{font-size:12px;font-weight:700;color:#1a1a3e;margin:18px 0 6px;text-transform:uppercase;letter-spacing:.5px}
table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:18px}
thead tr{background:#1a1a3e;color:#fff}
th{padding:8px 10px;text-align:left;font-weight:600;font-size:11px}
th.r,td.r{text-align:right}
td{padding:6px 10px;border-bottom:1px solid #e8e8e8}
tr:nth-child(even) td{background:#f5f5fb}
.total td{background:#dde;font-weight:700;border-top:2px solid #1a1a3e;font-size:12px}
.footer{margin-top:20px;font-size:10px;color:#aaa;text-align:center}
</style></head><body>
<div class="header">
  <div>
    <div class="logo">🎳 KUGLANJE ARENA</div>
    <h1>${igrac.ime}</h1>
    <div class="subtitle">${igrac.klub} &nbsp;·&nbsp; Statistika treninga &nbsp;·&nbsp; ${period}</div>
  </div>
  <div class="meta">Generirano: ${genDate}<br>Broj treninga: <strong>${n}</strong></div>
</div>
<div class="stat-grid">
  <div class="stat-box"><div class="stat-num">${n}</div><div class="stat-lbl">Treninga</div></div>
  <div class="stat-box"><div class="stat-num">${Math.round(sumU/n)}</div><div class="stat-lbl">Ø Ukupno</div></div>
  <div class="stat-box"><div class="stat-num">${Math.round(sumU/n/4)}</div><div class="stat-lbl">Ø po setu</div></div>
  <div class="stat-box"><div class="stat-num">${fmt(Math.round(sumP/n*10)/10)}</div><div class="stat-lbl">Ø Pune</div></div>
  <div class="stat-box"><div class="stat-num">${fmt(Math.round(sumC/n*10)/10)}</div><div class="stat-lbl">Ø Čišćenje</div></div>
  <div class="stat-box"><div class="stat-num">${Math.round(sumPr/n*10)/10}</div><div class="stat-lbl">Ø Promašaji</div></div>
</div>
${svgGrafikon}
<table>
  <thead><tr><th>#</th><th>Datum</th><th>Klub</th><th class="r">Ukupno</th><th class="r">Pune</th><th class="r">Čišćenje</th><th class="r">Promašaji</th><th class="r">Prosjek/set</th></tr></thead>
  <tbody>
    ${redovi.map((r,i) => `<tr>
      <td>${i+1}</td><td>${r.datum}</td><td>${r.klub}</td>
      <td class="r" style="font-weight:700">${r.ukupno}</td>
      <td class="r">${fmt(r.pune)}</td><td class="r">${fmt(r.cis)}</td>
      <td class="r">${r.promasaji}</td><td class="r">${r.prosjek}</td>
    </tr>`).join('')}
    <tr class="total">
      <td colspan="3">PROSJEK</td>
      <td class="r">${Math.round(sumU/n)}</td>
      <td class="r">${fmt(Math.round(sumP/n*10)/10)}</td>
      <td class="r">${fmt(Math.round(sumC/n*10)/10)}</td>
      <td class="r">${Math.round(sumPr/n*10)/10}</td>
      <td class="r">${Math.round(sumU/n/4)}</td>
    </tr>
  </tbody>
</table>
<div class="footer">kuglanje-arena.web.app</div>
</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const trendBoja = trend === 'up' ? '#2ed573' : trend === 'down' ? '#e94560' : '#666688';
  const trendIkona = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';

  const tabovi = [
    { id: 'statistike', naziv: 'Statistike' },
    { id: 'forma', naziv: 'Forma' },
    { id: 'utakmice', naziv: 'Utakmice' },
    { id: 'treninzi', naziv: 'Treninzi' },
    { id: 'grafikon-utakmice', naziv: 'Grafikon utakmica' },
    { id: 'grafikon-treninzi', naziv: 'Grafikon treninga' },
  ];

  // Koji klub je igrac zastupao u odabranoj sezoni
  const klubUOvojSezoni = utakmiceIgraca.length > 0
    ? utakmiceIgraca[utakmiceIgraca.length - 1].klub
    : igrac.klub;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <button className="btn-secondary btn-sm"
          onClick={() => setAktivna(povratakNa || { strana: 'igraci' })}>← Nazad</button>
        <div style={{ display: 'flex', gap: '6px' }}>
          {SEZONE.map(s => (
            <button key={s} className={'filter-btn ' + (filterSezona === s ? 'active' : '')}
              onClick={() => setFilterSezona(s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            {igrac.slika ? (
              <img src={igrac.slika} alt={igrac.ime}
                style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e94560' }} />
            ) : (
              <div style={{ width: '90px', height: '90px', borderRadius: '50%', background: '#12122a', border: '3px solid #e94560', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', color: '#e94560', flexShrink: 0 }}>
                {igrac.ime.charAt(0)}
              </div>
            )}
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#fff' }}>{igrac.ime}</div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="badge badge-blue">{klubUOvojSezoni}</span>
                {klubUOvojSezoni !== igrac.klub && (
                  <span className="badge" style={{ background: '#2a2a4a', color: '#aaa', fontSize: '10px' }}>
                    trenutno: {igrac.klub}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#e94560' }}>{prosjekPoSetu}</div>
              <div style={{ fontSize: '12px', color: '#666688' }}>prosjek/set</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: trendBoja }}>{trendIkona}</div>
              <div style={{ fontSize: '12px', color: '#666688' }}>trend</div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {forma.map((f, i) => (
                <div key={i} style={{
                  width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: f.rezultatEkipe === 'P' ? '#2ed573' : f.rezultatEkipe === 'R' ? '#5378ff' : '#e94560',
                  fontSize: '12px', fontWeight: '700', color: '#fff'
                }} title={f.datum + ' vs ' + f.protivnik + ' — ' + f.ukupno}>
                  {f.rezultatEkipe}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {tabovi.map(t => (
          <button key={t.id}
            className={aktivniTab === t.id ? 'btn-primary btn-sm' : 'filter-btn'}
            onClick={() => setAktivniTab(t.id)}>
            {t.naziv}
          </button>
        ))}
      </div>

      {aktivniTab === 'statistike' && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[
            { naziv: 'Nastupa', vrijednost: utakmiceIgraca.length },
            { naziv: 'Treninzi', vrijednost: treninziIgraca.length },
            { naziv: 'Prosjek/set', vrijednost: prosjekPoSetu },
            { naziv: 'Prosjek/utakm.', vrijednost: prosjekCunjevaPoUtakmici },
            { naziv: 'Max utakmica', vrijednost: maxUkupno },
            { naziv: 'Ukupno SP', vrijednost: ukupnoSP },
            { naziv: 'Ukupno MP', vrijednost: ukupnoMP },
            { naziv: 'Ø Promašaji', vrijednost: prosjekPromasaja },
            { naziv: 'Prosjek trening', vrijednost: prosjekTrening },
          ].map(s => (
            <div key={s.naziv} className="stat-card">
              <div className="broj">{s.vrijednost}</div>
              <div className="naziv">{s.naziv}</div>
            </div>
          ))}
        </div>
      )}

      {aktivniTab === 'forma' && (
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="section-title">Forma — posljednjih 5 utakmica</div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {forma.length === 0 ? (
                <p style={{ color: '#666688' }}>Nema dovoljno utakmica za prikaz forme.</p>
              ) : (
                forma.map((f, i) => (
                  <div key={i} style={{ background: '#12122a', borderRadius: '8px', padding: '12px 16px', border: '1px solid #2a2a4a', minWidth: '150px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#666688' }}>{f.datum}</div>
                      <div style={{
                        width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: f.rezultatEkipe === 'P' ? '#2ed573' : f.rezultatEkipe === 'R' ? '#5378ff' : '#e94560',
                        fontSize: '11px', fontWeight: '700', color: '#fff'
                      }}>
                        {f.rezultatEkipe}
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#666688', marginBottom: '4px' }}>vs {f.protivnik}</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#e94560' }}>{f.ukupno}</div>
                    <div style={{ fontSize: '11px', color: '#666688' }}>cunjeva</div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#0f0f1a', borderRadius: '6px' }}>
              <div style={{ fontSize: '28px', color: trendBoja, fontWeight: '700' }}>{trendIkona}</div>
              <div>
                <div style={{ color: trendBoja, fontWeight: '600', fontSize: '14px' }}>
                  {trend === 'up' ? 'Forma u porastu' : trend === 'down' ? 'Forma u padu' : 'Stabilna forma'}
                </div>
                <div style={{ color: '#666688', fontSize: '12px' }}>
                  Prosjek zadnje 3 utakmice vs. prosjek sezone (±10)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {aktivniTab === 'utakmice' && (
        <div>
          {utakmiceIgraca.length === 0 ? (
            <div className="card empty-state">
              <div className="icon">🎳</div>
              <p>Nema odigranih utakmica.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[...utakmiceIgraca].reverse().map(u => {
                const r = getRezultatIgraca(u);
                return (
                  <div key={u.id} className="card"
                    onClick={() => setAktivna({ strana: 'detaljiUtakmice', utakmicaId: u.id })}
                    style={{ cursor: 'pointer', padding: '14px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#fff' }}>{u.domaci ? u.klub + " (D) vs " + u.protivnik + " (G)" : u.protivnik + " (D) vs " + u.klub + " (G)"}</div>
                        <div style={{ fontSize: '12px', color: '#666688', marginTop: '3px' }}>
                          Kolo {u.kolo} • {u.datum} • {u.sezona} • {u.domaci ? "(D)" : "(G)"}
                          {r?.izmjena && (
                            <span style={{ color: r.izmjena.tip === 'izlaz' ? '#e94560' : '#2ed573', marginLeft: '8px' }}>
                              {r.izmjena.tip === 'izlaz'
                                ? '↑ izlaz hitac ' + (r.izmjena.ukupnoHitac || ((r.izmjena.set - 1) * 30 + r.izmjena.bacanje))
                                : '↓ ulaz hitac ' + (r.izmjena.ukupnoHitac || ((r.izmjena.set - 1) * 30 + r.izmjena.bacanje))}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: '#e94560' }}>{r?.ukupno || 0}</div>
                          <div style={{ fontSize: '11px', color: '#666688' }}>cunjeva</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#fff' }}>{r?.sp || 0}</div>
                          <div style={{ fontSize: '11px', color: '#666688' }}>SP</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#fff' }}>{r?.mp || 0}</div>
                          <div style={{ fontSize: '11px', color: '#666688' }}>MP</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {aktivniTab === 'treninzi' && (
        <div>
          {treningStats && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#e94560', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                Statistika treninga — {filterSezona}
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {[
                  { naziv: 'Nastupa', vrijednost: treningStats.nastupa },
                  { naziv: 'Ø Ukupno', vrijednost: treningStats.prosjekUkupno },
                  { naziv: 'Ø po setu', vrijednost: treningStats.prosjekPoSetu },
                  { naziv: 'Ø Pune', vrijednost: treningStats.prosjekPune || '—' },
                  { naziv: 'Ø Čišćenje', vrijednost: treningStats.prosjekCis || '—', boja: '#2ed573' },
                  { naziv: 'Ø Promašaji', vrijednost: treningStats.prosjekPromasaji, boja: '#666688' },
                ].map(s => (
                  <div key={s.naziv} className="stat-card">
                    <div className="broj" style={s.boja ? { color: s.boja } : {}}>{s.vrijednost}</div>
                    <div className="naziv">{s.naziv}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {treninziIgraca.length === 0 ? (
            <div className="card empty-state">
              <div className="icon">🏋️</div>
              <p>Nema unesenih treninga.</p>
            </div>
          ) : (
            [...treninziIgraca].reverse().map(t => {
              const r = getTreningRezultat(t);
              const jeEditMode = editTrening?.id === t.id;

              return (
                <div key={t.id} className="card" style={{ padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: jeEditMode ? '16px' : '0' }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#fff' }}>{t.datum}</div>
                      <div style={{ fontSize: '12px', color: '#666688', marginTop: '2px' }}>{t.klub} • {t.sezona}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      {!jeEditMode && (
                        <>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#e94560' }}>{r?.ukupno || 0}</div>
                            <div style={{ fontSize: '11px', color: '#666688' }}>cunjeva</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>{r?.promasaji || 0}</div>
                            <div style={{ fontSize: '11px', color: '#666688' }}>promasaji</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>{Math.round((r?.ukupno || 0) / 4)}</div>
                            <div style={{ fontSize: '11px', color: '#666688' }}>prosjek/set</div>
                          </div>
                        </>
                      )}
                      {!jeGost && (
                        <button
                          className={jeEditMode ? 'btn-secondary btn-sm' : 'btn-edit btn-sm'}
                          onClick={() => {
                            if (jeEditMode) {
                              setEditTrening(null);
                            } else {
                              setEditTrening({ id: t.id, ukupno: r?.ukupno || '', promasaji: r?.promasaji || '', napredna: r?.napredna || false, setovi: r?.setovi || {} });
                            }
                          }}>
                          {jeEditMode ? 'Odustani' : '✏️ Uredi'}
                        </button>
                      )}
                    </div>
                  </div>
                  {jeEditMode && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ fontSize: '13px', color: '#666688' }}>Uredi rezultat</div>
                        <button onClick={() => setEditTrening(prev => ({ ...prev, napredna: !prev.napredna }))}
                          style={{ padding: '4px 12px', fontSize: '12px', borderRadius: '6px', background: editTrening.napredna ? '#e94560' : '#16162a', color: editTrening.napredna ? 'white' : '#888899', border: '1px solid #2a2a4a', cursor: 'pointer' }}>
                          {editTrening.napredna ? 'Osnovno' : 'Po setovima'}
                        </button>
                      </div>
                      {!editTrening.napredna ? (
                        <div className="form-row" style={{ marginBottom: '14px' }}>
                          <div className="form-group">
                            <label>Ukupno cunjeva</label>
                            <input type="number" min="0" step="0.5" value={editTrening.ukupno}
                              onChange={e => setEditTrening(prev => ({ ...prev, ukupno: e.target.value }))}
                              style={{ width: '140px' }} />
                          </div>
                          <div className="form-group">
                            <label>Promasaji</label>
                            <input type="number" min="0" value={editTrening.promasaji}
                              onChange={e => setEditTrening(prev => ({ ...prev, promasaji: e.target.value }))}
                              style={{ width: '120px' }} />
                          </div>
                        </div>
                      ) : (
                        <table style={{ marginBottom: '14px' }}>
                          <thead>
                            <tr>
                              <th>Set</th>
                              <th style={{ textAlign: 'center' }}>Pune</th>
                              <th style={{ textAlign: 'center' }}>Ciscenje</th>
                              <th style={{ textAlign: 'center' }}>Promasaji</th>
                              <th style={{ textAlign: 'center' }}>Ukupno</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[1, 2, 3, 4].map(s => {
                              const set = editTrening.setovi?.[s] || {};
                              const pune = parseFloat(set.pune) || 0;
                              const cis = parseFloat(set.cis) || 0;
                              return (
                                <tr key={s}>
                                  <td style={{ color: '#e94560', fontWeight: '600' }}>Set {s}</td>
                                  <td style={{ padding: '4px', textAlign: 'center' }}>
                                    <input type="number" min="0" step="0.5" value={set.pune || ''}
                                      onChange={e => setEditTrening(prev => ({ ...prev, setovi: { ...prev.setovi, [s]: { ...prev.setovi?.[s], pune: e.target.value } } }))}
                                      style={{ width: '60px', textAlign: 'center' }} />
                                  </td>
                                  <td style={{ padding: '4px', textAlign: 'center' }}>
                                    <input type="number" min="0" step="0.5" value={set.cis || ''}
                                      onChange={e => setEditTrening(prev => ({ ...prev, setovi: { ...prev.setovi, [s]: { ...prev.setovi?.[s], cis: e.target.value } } }))}
                                      style={{ width: '60px', textAlign: 'center' }} />
                                  </td>
                                  <td style={{ padding: '4px', textAlign: 'center' }}>
                                    <input type="number" min="0" value={set.promasaji || ''}
                                      onChange={e => setEditTrening(prev => ({ ...prev, setovi: { ...prev.setovi, [s]: { ...prev.setovi?.[s], promasaji: e.target.value } } }))}
                                      style={{ width: '60px', textAlign: 'center' }} />
                                  </td>
                                  <td style={{ textAlign: 'center', fontWeight: '700', color: '#e94560' }}>{pune + cis}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                      <button className="btn-primary" onClick={() => {
                        let ukupno, promasaji;
                        if (editTrening.napredna) {
                          ukupno = [1, 2, 3, 4].reduce((acc, s) => {
                            const set = editTrening.setovi?.[s] || {};
                            return acc + (parseFloat(set.pune) || 0) + (parseFloat(set.cis) || 0);
                          }, 0);
                          promasaji = [1, 2, 3, 4].reduce((acc, s) => acc + (parseFloat(editTrening.setovi?.[s]?.promasaji) || 0), 0);
                        } else {
                          ukupno = parseFloat(editTrening.ukupno) || 0;
                          promasaji = parseFloat(editTrening.promasaji) || 0;
                        }
                        handleSacuvajTrening(t.id, { ukupno, promasaji, napredna: editTrening.napredna, setovi: editTrening.napredna ? editTrening.setovi : null });
                      }}>
                        Sacuvaj izmjenu
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
          </div>
        </div>
      )}

      {aktivniTab === 'grafikon-utakmice' && (
        <div>
          {podaciUtakmica.length === 0 ? (
            <div className="card empty-state"><div className="icon">📊</div><p>Nema podataka za grafikon.</p></div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <div className="stat-card"><div className="broj">{prosjekUtakmicaGraf}</div><div className="naziv">Ø prosjek/set</div></div>
                <div className="stat-card"><div className="broj" style={{ color: '#2ed573' }}>{maxEkv}</div><div className="naziv">Najbolja utakmica</div></div>
                <div className="stat-card"><div className="broj" style={{ color: '#e94560' }}>{minEkv}</div><div className="naziv">Najlošija utakmica</div></div>
              </div>
              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="section-title">Ukupno cunjeva po utakmici</div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={podaciUtakmica} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={prosjekCunjevaPoUtakmici} stroke="#5378ff" strokeDasharray="6 3"
                      label={{ value: 'Prosjek: ' + prosjekCunjevaPoUtakmici, fill: '#5378ff', fontSize: 12, position: 'right' }} />
                    <Line type="monotone" dataKey="ukupno" stroke="#e94560" strokeWidth={2.5}
                      dot={{ r: 5, fill: '#e94560', strokeWidth: 2 }} activeDot={{ r: 7 }} name="Ukupno cunjeva" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="section-title">Prosjek po setu</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={podaciUtakmica} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="prosjek" stroke="#2ed573" strokeWidth={2.5}
                      dot={{ r: 5, fill: '#2ed573', strokeWidth: 2 }} activeDot={{ r: 7 }} name="Prosjek/set" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <div className="section-title">Promasaji po utakmici</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={podaciUtakmica} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="promasaji" stroke="#ff6b6b" strokeWidth={2.5}
                      dot={{ r: 5, fill: '#ff6b6b', strokeWidth: 2 }} activeDot={{ r: 7 }} name="Promasaji" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}

      {aktivniTab === 'grafikon-treninzi' && (
        <div>
          {podaciTreninzi.length === 0 ? (
            <div className="card empty-state"><div className="icon">📊</div><p>Nema podataka za grafikon.</p></div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <div className="stat-card"><div className="broj">{prosjekTreningGraf}</div><div className="naziv">Ø Ukupno</div></div>
                <div className="stat-card"><div className="broj">{Math.round(prosjekTreningGraf / 4)}</div><div className="naziv">Ø po setu</div></div>
                <div className="stat-card"><div className="broj" style={{ color: '#2ed573' }}>{Math.max(...podaciTreninzi.map(d => d.ukupno))}</div><div className="naziv">Najbolji</div></div>
                <div className="stat-card"><div className="broj" style={{ color: '#e94560' }}>{Math.min(...podaciTreninzi.map(d => d.ukupno))}</div><div className="naziv">Najlošiji</div></div>
              </div>

              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="section-title">Ukupno cunjeva po treningu</div>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={podaciTreninzi} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={prosjekTreningGraf} stroke="#5378ff" strokeDasharray="6 3"
                      label={{ value: 'Ø ' + prosjekTreningGraf, fill: '#5378ff', fontSize: 11, position: 'right' }} />
                    <Line type="monotone" dataKey="ukupno" stroke="#ffc107" strokeWidth={2.5}
                      dot={{ r: 4, fill: '#ffc107' }} activeDot={{ r: 7 }} name="Ukupno" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="section-title">Pune po treningu</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={podaciTreninzi} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="pune" stroke="#5378ff" strokeWidth={2.5}
                      dot={{ r: 4, fill: '#5378ff' }} activeDot={{ r: 7 }} name="Pune" connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="section-title">Čišćenje po treningu</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={podaciTreninzi} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="cis" stroke="#2ed573" strokeWidth={2.5}
                      dot={{ r: 4, fill: '#2ed573' }} activeDot={{ r: 7 }} name="Čišćenje" connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="card" style={{ marginBottom: '20px' }}>
                <div className="section-title">Promašaji po treningu</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={podaciTreninzi} margin={{ top: 10, right: 10, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                    <XAxis dataKey="datum" stroke="#666688" fontSize={11} angle={-45} textAnchor="end" interval={0} />
                    <YAxis stroke="#666688" fontSize={12} domain={['auto', 'auto']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="promasaji" stroke="#ff6b6b" strokeWidth={2.5}
                      dot={{ r: 4, fill: '#ff6b6b' }} activeDot={{ r: 7 }} name="Promašaji" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Izvoz statistike */}
              <div className="card">
                <div className="section-title" style={{ marginBottom: '12px' }}>📄 Izvoz statistike treninga</div>
                <div className="form-row" style={{ alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                  <div className="form-group">
                    <label>Od datuma</label>
                    <input type="date" value={exportOd}
                      onChange={e => setExportOd(e.target.value)}
                      style={{ width: '160px' }} />
                  </div>
                  <div className="form-group">
                    <label>Do datuma</label>
                    <input type="date" value={exportDo}
                      onChange={e => setExportDo(e.target.value)}
                      style={{ width: '160px' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <button className="btn-primary" onClick={izvezi}
                      style={{ background: '#ffc107', borderColor: '#ffc107', color: '#000', fontWeight: '700' }}>
                      🖨️ Štampaj / Izvezi PDF
                    </button>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: '#666688', marginTop: '10px' }}>
                  Bez odabranog perioda — izvozi sve treninge. Format A4, spreman za štampu ili PDF.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Profil;
